import imageCompression from 'browser-image-compression';

/**
 * Comprime uma imagem se ela for maior que 1MB
 * Mantém alta qualidade e resolução para exibição em telões
 * 
 * @param file - Arquivo de imagem a ser comprimido
 * @returns Promise<File> - Arquivo comprimido ou original se já estava abaixo do limite
 */
export async function compressImageIfNeeded(file: File): Promise<File> {
  const MAX_SIZE_MB = 1;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  // Se a imagem já está abaixo do limite, retorna sem comprimir
  if (file.size <= MAX_SIZE_BYTES) {
    return file;
  }

  // Opções de compressão otimizadas para telões (alta qualidade e resolução)
  const options = {
    maxSizeMB: MAX_SIZE_MB, // Tamanho máximo desejado em MB
    maxWidthOrHeight: 4096, // Resolução máxima mantida (adequada para telões)
    useWebWorker: true, // Usa Web Worker para não bloquear a UI
    quality: 0.85, // Qualidade alta (0-1, onde 1 é máxima qualidade)
    fileType: file.type, // Mantém o tipo original da imagem
  };

  try {
    const compressedFile = await imageCompression(file, options);
    
    // Log para debug (pode ser removido em produção)
    console.log(
      `Imagem comprimida: ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`
    );
    
    return compressedFile;
  } catch (error) {
    console.error('Erro ao comprimir imagem:', error);
    // Em caso de erro, retorna o arquivo original
    return file;
  }
}

/**
 * Converte um arquivo File para base64 string
 * 
 * @param file - Arquivo a ser convertido
 * @returns Promise<string> - String base64
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Informações sobre o processamento da imagem
 */
export interface ImageProcessInfo {
  base64: string;
  originalSizeMB: number;
  compressedSizeMB: number;
  wasCompressed: boolean;
}

/**
 * Processa uma imagem: comprime se necessário e converte para base64
 * 
 * @param file - Arquivo de imagem
 * @returns Promise<ImageProcessInfo> - Informações sobre a imagem processada
 */
export async function processImage(file: File): Promise<ImageProcessInfo> {
  const originalSizeMB = file.size / 1024 / 1024;
  const compressedFile = await compressImageIfNeeded(file);
  const compressedSizeMB = compressedFile.size / 1024 / 1024;
  const wasCompressed = compressedFile.size < file.size;
  const base64 = await fileToBase64(compressedFile);
  
  return {
    base64,
    originalSizeMB,
    compressedSizeMB,
    wasCompressed,
  };
}
